import { Chip, IconButton, Link, Stack, Tooltip, Typography, useMediaQuery } from '@mui/material'
import { CheckCircleOutlined, DriveFileRenameOutlineOutlined, EditOutlined, TuneOutlined, UndoOutlined } from '@mui/icons-material'
import { FunctionComponent, useMemo } from 'react'
import gaiaTheme from '../../../../root-components/theme'
import { IHeadCell } from '../../../../shared/interfaces/table-display.model'
import { INominalCodeMapping, INominalSummaryRow } from '../../../../shared/services/nominal-code-mapping/nominal-code-mapping.model'
import { ISpendBasedConversion } from '../../../../shared/services/spend-based-conversion/spend-based-conversion.model'
import { IConversionSource } from '../../../../shared/services/conversion-source/conversion-source.model'
import { IGroup } from '../../../../shared/services/group/group.model'
import {
  INominalRowEdit,
  canAcceptSuggestion,
  findSuggestedActivity,
  formatNominalActivityName,
  getSuggestionChipLabel,
  mappingToOutcomeValue,
  normaliseNominalCode,
} from '../../../../shared/utils/nominal-code-utils'
import { TableDisplay } from '../../miscellaneous-components/table-display'
import { TableActionButtons } from '../../miscellaneous-components/table-action-buttons'
import { INominalOutcomeValue } from './nominal-mapping-outcome-picker'
import { INominalActivityOption, NominalOutcomeAutocomplete } from './nominal-outcome-autocomplete'

/**
 * NominalCodeMappingTable
 * -------------------------------------------------------------------------
 * Every code to map, one row each, with the money seen on it and its outcome. Shared by the Chart
 * of Accounts page and the import wizard's validation step, through NominalCodeMappingWorkspace.
 * The parent owns the rows (already filtered and searched), the unsaved edits, which saved rows are
 * open for editing, and the selection; this component only renders them and reports back.
 *
 * The Outcome cell says where a row stands without a status column: a saved outcome reads as text
 * (click it, or "Change outcome" in the row menu, to change it), an empty picker needs a decision,
 * and a row with an unsaved change shows its picker with an "Unsaved" chip. An open suggestion sits
 * above the picker until it is accepted.
 *
 * Paged through TableDisplay, so a two-thousand-code Sage 200 nominal list mounts one page of
 * outcome controls, not all of them. Sorted by spend seen, largest first: the codes carrying the
 * money are the job.
 */

const qualifierText = (qualifiers?: string[] | string | null): string => {
  if (!qualifiers) return ''
  return Array.isArray(qualifiers) ? qualifiers.filter(Boolean).join(', ') : qualifiers
}

// What a saved mapping reads as when the row is not being edited.
const savedOutcomeText = (
  mapping: INominalCodeMapping,
  conversion: ISpendBasedConversion | null,
  groupName: string | null,
  sourceName: string | null,
): { text: string, caption: string } => {
  if (mapping.mappingType === 'exclude') {
    const reason = (mapping.excludeReason || '').trim()
    return { text: reason ? `Excluded · ${reason}` : 'Excluded', caption: '' }
  }
  if (mapping.mappingType === 'private') return { text: "Don't import", caption: 'Never brought in from your ledger' }
  if (mapping.mappingType === 'activity') return { text: 'Captured via activity data (retired)', caption: '' }
  return {
    text: formatNominalActivityName(conversion) || 'Spend activity (from another factor set)',
    caption: [groupName, sourceName].filter(Boolean).join(' · '),
  }
}

export const NominalCodeMappingTable: FunctionComponent<{
  rows: INominalSummaryRow[],
  // Unsaved edits keyed by normalised code.
  edits: Record<string, INominalRowEdit>,
  onChange: (code: string, next: INominalOutcomeValue, conversion: ISpendBasedConversion | null) => void,
  selected: string[],
  setSelected: React.Dispatch<React.SetStateAction<string[]>>,
  // Whether the rows carry a checkbox for the bulk bar.
  selectable: boolean,
  // Saved rows put back into edit mode, keyed by normalised code.
  editingKeys: Set<string>,
  onStartEditing: (code: string) => void,
  onAcceptSuggestion: (code: string) => void,
  onOpenAdvanced: (code: string) => void,
  // Puts a row back as it was loaded.
  onUndoChange?: (code: string) => void,
  // Omitted on the import step, where saved mappings are not managed.
  onDeleteMapping?: (code: string) => void,
  // Opens the single-code dialog for a saved mapping, for its code and description.
  onEditMapping?: (code: string) => void,
  defaultSourceId: string | null,
  activities: INominalActivityOption[],
  sources: IConversionSource[],
  groups: IGroup[],
  // Only when the organisation uses more than one spend source.
  showSourceChip: boolean,
  activitiesLoading?: boolean,
  loading?: boolean,
}> = ({
  rows,
  edits,
  onChange,
  selected,
  setSelected,
  selectable,
  editingKeys,
  onStartEditing,
  onAcceptSuggestion,
  onOpenAdvanced,
  onUndoChange,
  onDeleteMapping,
  onEditMapping,
  defaultSourceId,
  activities,
  sources,
  groups,
  showSourceChip,
  activitiesLoading = false,
  loading = false,
}) => {
  const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('md'))

  const activityConversions = useMemo(() => activities.map(option => option.conversion), [activities])
  const activityById = useMemo(() => new Map(activityConversions.map(conversion => [conversion.id, conversion])), [activityConversions])
  const sourceNames = useMemo(() => new Map(sources.map(source => [source.id, source.source])), [sources])
  const groupNames = useMemo(() => new Map(groups.map(group => [group.id, group.title])), [groups])

  const headCells: IHeadCell[] = useMemo(() => [
    //Action first, as every list in the app has it, so the menu stays reachable on a phone.
    { id: 'action', numeric: false, label: 'Action', sortable: false, includeInMobile: false },
    { id: 'nominalCode', numeric: false, label: 'Code', sortable: true, sortValue: 'codeSort', isHeading: true },
    { id: 'nominalDescription', numeric: false, label: 'Account', sortable: true, sortValue: 'descriptionSort' },
    { id: 'outcome', numeric: false, label: 'Outcome', sortable: false, oneRow: true },
  ], [])

  // One value object per saved mapping, kept while the rows are, so an untouched row hands its
  // memoised outcome control the same value on every render and is not re-rendered by an edit
  // somewhere else in the table.
  const savedValues = useMemo(() => new Map(rows.map(row => [normaliseNominalCode(row.nominalCode), mappingToOutcomeValue(row.mapping)])), [rows])

  const tableRows = useMemo(() => rows.map(row => {
    const key = normaliseNominalCode(row.nominalCode)
    const edit = edits[key]
    const isEditing = editingKeys.has(key)
    // A saved row reads as text until it is changed or put back into edit mode.
    const showPicker = !row.mapping || !!edit || isEditing
    const value = edit ? edit.value : (savedValues.get(key) ?? null)
    const savedConversion = row.mapping?.spendBasedConversion
      ?? (row.mapping?.spendBasedConversionId ? activityById.get(row.mapping.spendBasedConversionId) ?? null : null)
    const currentConversion = edit ? (edit.conversion ?? null) : savedConversion
    const spendTotal = Number(row.spend?.total) || 0
    const qualifiers = qualifierText(row.ledger?.qualifiers)
    const sourceId = value?.mappingType === 'spend' ? (value.conversionSourceId || defaultSourceId) : null
    const sourceName = showSourceChip && sourceId ? sourceNames.get(sourceId) ?? null : null
    // The suggestion goes once the row is decided: edited here, or saved earlier.
    const showSuggestion = !!row.suggestion && !row.mapping && !edit
    // A spend suggestion is accepted as an activity in the organisation's own factor set (by id,
    // else by name). One that is not there keeps its chip but cannot be ticked.
    const acceptable = showSuggestion && canAcceptSuggestion(row.suggestion, activityConversions)
    const acceptTooltip = acceptable
      ? 'Accept suggestion'
      : (activitiesLoading ? 'Loading your activities' : 'This activity is not in your factor set for this year')

    let outcome: React.ReactNode
    if (!showPicker && row.mapping) {
      const saved = savedOutcomeText(
        row.mapping,
        savedConversion,
        row.mapping.group?.title ?? (row.mapping.groupId ? groupNames.get(row.mapping.groupId) ?? null : null),
        sourceName,
      )
      outcome = (
        <Stack spacing={0.25} alignItems='flex-start'>
          <Tooltip title='Change outcome'>
            <Link
              component='button'
              type='button'
              variant='body2'
              underline='hover'
              color='text.primary'
              onClick={() => onStartEditing(key)}
              sx={{ textAlign: 'left', wordBreak: 'break-word' }}>
              {saved.text}
            </Link>
          </Tooltip>
          {saved.caption && <Typography variant='caption' color='text.secondary'>{saved.caption}</Typography>}
        </Stack>
      )
    } else {
      const suggestedActivity = showSuggestion && row.suggestion ? findSuggestedActivity(row.suggestion, activityConversions) : null
      outcome = (
        <Stack spacing={0.75} sx={{ minWidth: 240 }}>
          {showSuggestion && row.suggestion && (
            <Stack direction='row' spacing={0.5} alignItems='center'>
              {/* Visible as a suggestion with its reason, so it reads as something to check
                  rather than a decision already taken. Nothing saves until it is accepted. */}
              <Tooltip title={row.suggestion.reason}>
                <Chip
                  size='small'
                  variant='outlined'
                  // Low confidence reads as a question in the default colour; medium and high as a
                  // suggestion in warning.
                  color={row.suggestion.confidence === 'low' ? 'default' : 'warning'}
                  label={getSuggestionChipLabel(row.suggestion, formatNominalActivityName(suggestedActivity))}
                  // Activity names run long, so the label wraps rather than being cut off.
                  sx={{ maxWidth: 320, height: 'auto', py: 0.25, '& .MuiChip-label': { whiteSpace: 'normal' } }}
                />
              </Tooltip>
              <Tooltip title={acceptTooltip}>
                <span>
                  <IconButton
                    size='small'
                    color='primary'
                    disabled={!acceptable}
                    onClick={() => onAcceptSuggestion(key)}
                    aria-label={`Accept suggestion for code ${row.nominalCode}`}>
                    <CheckCircleOutlined fontSize='small' />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          )}
          <NominalOutcomeAutocomplete
            id={key}
            value={value}
            onChange={onChange}
            activities={activities}
            currentConversion={currentConversion}
            sourceLabel={sourceName}
            loading={activitiesLoading}
            codeLabel={`code ${row.nominalCode}`}
            autoFocus={isEditing && !edit}
          />
          {edit && <Chip size='small' variant='outlined' color='primary' label='Unsaved' sx={{ alignSelf: 'flex-start' }} />}
        </Stack>
      )
    }

    // A saved row not being edited: change it, open Advanced, edit its details, or delete it.
    // Anything else: Advanced, and Undo while there is something to undo.
    const isSavedText = !!row.mapping && !showPicker
    const advancedItem = { label: 'Advanced', icon: <TuneOutlined sx={{ mr: 1 }} />, onClick: () => onOpenAdvanced(key) }
    const menuItems = isSavedText
      ? [
        { label: 'Change outcome', icon: <EditOutlined sx={{ mr: 1 }} />, onClick: () => onStartEditing(key) },
        advancedItem,
        ...(onEditMapping ? [{ label: 'Edit details', icon: <DriveFileRenameOutlineOutlined sx={{ mr: 1 }} />, onClick: () => onEditMapping(key) }] : []),
      ]
      : [
        advancedItem,
        ...(onUndoChange && (edit || (row.mapping && isEditing))
          ? [{ label: 'Undo change', icon: <UndoOutlined sx={{ mr: 1 }} />, onClick: () => onUndoChange(key) }]
          : []),
      ]

    return {
      id: key,
      codeSort: normaliseNominalCode(row.nominalCode),
      descriptionSort: normaliseNominalCode(row.nominalDescription),
      //Not a column any more, but still the order: the codes carrying the most spend come first
      //and the accounts nothing has posted to sink to the bottom.
      spendSort: Math.abs(spendTotal),
      nominalCode: (
        <Stack spacing={0.5} alignItems='flex-start'>
          <Typography variant='body2' color='text.primary' sx={{ wordBreak: 'break-word' }}>{row.nominalCode}</Typography>
          {row.ledger?.archived && <Chip size='small' variant='outlined' label='Archived' />}
          {qualifiers && <Typography variant='caption' color='text.secondary'>{qualifiers}</Typography>}
        </Stack>
      ),
      nominalDescription: row.nominalDescription || '',
      outcome,
      action: (
        <TableActionButtons
          id={key}
          preferViewThenAdditionalThenDelete={true}
          additionalMenuItems={menuItems}
          includeDelete={isSavedText && !!onDeleteMapping}
          onDelete={() => onDeleteMapping?.(key)}
        />
      ),
    }
  }), [rows, edits, editingKeys, savedValues, activities, activityConversions, activityById, activitiesLoading, defaultSourceId, groupNames, onAcceptSuggestion, onChange, onDeleteMapping, onEditMapping, onOpenAdvanced, onStartEditing, onUndoChange, showSourceChip, sourceNames])

  return (
    <TableDisplay
      isMobileMode={isMobileMode}
      headCells={headCells}
      rows={tableRows}
      selected={selected}
      setSelected={setSelected}
      // Ticking rows is for the bulk bar, which only the "Needs a decision" view offers.
      haveCheckboxSelect={selectable}
      // Selection only through the checkbox, so clicking inside a row's outcome list or reason
      // field never ticks or unticks the row.
      selectByCheckboxOnly={true}
      perPage={25}
      defaultOrderBy='spendSort'
      defaultOrder='desc'
      loading={loading}
    />
  )
}
